import {
  type Channel,
  type InboxConversationDetail,
  type InboxIdentity,
  type InboxMemberSummary,
  channelHidesComments,
} from '@sbc/domain-models/gateway';
import { CheckCircle2, ExternalLink, RotateCcw, X } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ownershipConflictOf,
  useClaimInboxConversation,
  useInboxMember,
  useLinkInboxIdentity,
  useReleaseInboxConversation,
  useSetInboxMessageHidden,
  useUnlinkInboxIdentity,
  useUpdateInboxStatus,
} from '@/lib/api/inbox';

import { ConversationTags } from './conversation-tags';
import { InboxAvatar } from './inbox-avatar';
import {
  CHANNEL_NAMES,
  SURFACE_LABELS,
  contactPhone,
  displayName,
  latestCustomerComment,
  metaInboxLabel,
  openOriginalLabel,
  memberLinked,
} from './inbox-format';

function ExternalLinkButton({ href, label }: { href: string; label: string }) {
  return (
    <Button variant="outline" size="sm" className="w-full" asChild>
      <a href={href} target="_blank" rel="noreferrer">
        <ExternalLink className="size-4" />
        {label}
      </a>
    </Button>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 border-b px-4 py-4">
      <h3 className="text-xs font-semibold tracking-wide text-[var(--color-muted-foreground)] uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="shrink-0 text-[var(--color-muted-foreground)]">{label}</span>
      <span className="min-w-0 text-right break-words">{children}</span>
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatSnoozeEnd(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function ConversationDetails({
  conversation,
  onClose,
}: {
  conversation: InboxConversationDetail;
  onClose?: () => void;
}) {
  const name = displayName(conversation);
  const phone = contactPhone(conversation);
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-3 border-b px-4 py-3">
        <InboxAvatar name={name} channel={conversation.channel} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{name}</p>
          <p className="truncate text-xs text-[var(--color-muted-foreground)]">
            {CHANNEL_NAMES[conversation.channel]} · {SURFACE_LABELS[conversation.surface]}
            {phone ? ` · ${phone}` : ''}
          </p>
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            className="size-8 px-0"
            aria-label="Close details"
            onClick={onClose}
          >
            <X className="size-4" />
          </Button>
        )}
      </div>
      <div className="relative min-h-0 flex-1 overflow-y-auto">
        <OwnerSection conversation={conversation} />
        {conversation.surface === 'dm' && <MemberSection conversation={conversation} />}
        <StatusSection conversation={conversation} />
        <Section title="Tags">
          <ConversationTags conversation={conversation} />
        </Section>
        {conversation.surface === 'comment_thread' && (
          <ModerationSection conversation={conversation} />
        )}
        {(conversation.permalink || conversation.metaInboxLink) && (
          <div className="space-y-2 px-4 py-4">
            {conversation.metaInboxLink && (
              <ExternalLinkButton
                href={conversation.metaInboxLink.url}
                label={metaInboxLabel(conversation.metaInboxLink)}
              />
            )}
            {conversation.permalink && (
              <ExternalLinkButton
                href={conversation.permalink}
                label={openOriginalLabel(conversation.channel, conversation.surface)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function OwnerSection({ conversation }: { conversation: InboxConversationDetail }) {
  const claim = useClaimInboxConversation(conversation.id);
  const release = useReleaseInboxConversation(conversation.id);
  const [takeOverFrom, setTakeOverFrom] = useState<string | null>(null);
  const owner = conversation.assignee;

  const onClaim = (takeOver = false) => {
    setTakeOverFrom(null);
    claim.mutate(
      { takeOver: takeOver || undefined },
      {
        onError: (error) => {
          const conflict = ownershipConflictOf(error);
          if (conflict) {
            setTakeOverFrom(conflict.owner.email);
          }
        },
      },
    );
  };

  return (
    <Section title="Owner">
      <p className="text-sm">
        {owner ? (
          owner.isViewer ? (
            <span className="font-medium">You</span>
          ) : (
            owner.email
          )
        ) : (
          <span className="text-[var(--color-muted-foreground)]">Unassigned</span>
        )}
      </p>
      <div className="flex flex-wrap gap-2">
        {!owner && (
          <Button size="sm" disabled={claim.isPending} onClick={() => onClaim()}>
            Claim
          </Button>
        )}
        {owner?.isViewer && (
          <Button
            size="sm"
            variant="outline"
            disabled={release.isPending}
            onClick={() => release.mutate(undefined)}
          >
            Release
          </Button>
        )}
        {owner && !owner.isViewer && (
          <Button
            size="sm"
            variant="outline"
            disabled={claim.isPending}
            onClick={() => setTakeOverFrom(owner.email)}
          >
            Take over
          </Button>
        )}
      </div>
      {takeOverFrom && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border p-2.5 text-sm">
          <p>Take over from {takeOverFrom}?</p>
          <Button size="sm" disabled={claim.isPending} onClick={() => onClaim(true)}>
            Take over
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setTakeOverFrom(null)}>
            Cancel
          </Button>
        </div>
      )}
      {(claim.isError || release.isError) && !takeOverFrom && (
        <p className="text-sm text-[var(--color-destructive)]">
          {claim.error?.message ?? release.error?.message}
        </p>
      )}
    </Section>
  );
}

function StatusSection({ conversation }: { conversation: InboxConversationDetail }) {
  const updateStatus = useUpdateInboxStatus();
  const done = conversation.status === 'dismissed';
  return (
    <Section title="Status">
      <Button
        size="sm"
        variant="outline"
        className="w-full"
        disabled={updateStatus.isPending}
        onClick={() =>
          updateStatus.mutate({ id: conversation.id, status: done ? 'needs_reply' : 'dismissed' })
        }
      >
        {done ? <RotateCcw className="size-4" /> : <CheckCircle2 className="size-4" />}
        {done ? 'Reopen' : 'Mark done (E)'}
      </Button>
      {conversation.intercomSnoozedUntil && (
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Snoozed in Intercom until {formatSnoozeEnd(conversation.intercomSnoozedUntil)}
        </p>
      )}
      {updateStatus.isError && (
        <p className="text-sm text-[var(--color-destructive)]">{updateStatus.error.message}</p>
      )}
    </Section>
  );
}

function ModerationSection({ conversation }: { conversation: InboxConversationDetail }) {
  const setHidden = useSetInboxMessageHidden(conversation.id);
  const target = latestCustomerComment(conversation.surface, conversation.messages);
  const hiddenCount = conversation.messages.filter((message) => message.hideState !== null).length;
  if (!target || !channelHidesComments(conversation.channel)) {
    return null;
  }
  const hidden = target.hideState !== null;
  return (
    <Section title="Moderation">
      <p className="text-sm text-[var(--color-muted-foreground)]">
        {hidden
          ? 'The latest comment is hidden on the post. Its author still sees it.'
          : 'The latest comment is visible on the post.'}
        {hiddenCount > 0 && ` ${hiddenCount} hidden in this thread.`}
      </p>
      <Button
        size="sm"
        variant="outline"
        className="w-full"
        disabled={setHidden.isPending}
        onClick={() => setHidden.mutate({ messageId: target.id, hidden: !hidden })}
      >
        {hidden ? 'Unhide latest comment' : 'Hide latest comment'}
      </Button>
      {setHidden.isError && (
        <p className="text-sm text-[var(--color-destructive)]">{setHidden.error.message}</p>
      )}
    </Section>
  );
}

function MemberSection({ conversation }: { conversation: InboxConversationDetail }) {
  const identity = conversation.identity;
  const linked = memberLinked(conversation);
  const member = useInboxMember(conversation.id, linked);
  const unlink = useUnlinkInboxIdentity(conversation.id);
  return (
    <Section title="Member">
      {linked ? (
        <>
          {member.isPending && (
            <p className="text-sm text-[var(--color-muted-foreground)]">Loading account…</p>
          )}
          {member.data?.unavailable && (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              The SBC account could not be read right now.
            </p>
          )}
          {member.data?.member && <MemberFacts member={member.data.member} />}
          {identity?.confirmedVia === 'phone' && (
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Matched by WhatsApp number
            </p>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={unlink.isPending}
            onClick={() => unlink.mutate(undefined)}
          >
            {unlink.isPending ? 'Unlinking…' : 'Unlink account'}
          </Button>
        </>
      ) : (
        <IdentityLink conversation={conversation} identity={identity} />
      )}
    </Section>
  );
}

function MemberFacts({ member }: { member: InboxMemberSummary }) {
  const order = member.lastOrder;
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{member.name ?? member.email ?? 'SBC member'}</p>
      {member.name && member.email && (
        <p className="text-xs break-all text-[var(--color-muted-foreground)]">{member.email}</p>
      )}
      <Fact label="Plan">
        {member.plan ? (
          <>
            {member.plan.billingPlan ?? 'Subscription'}{' '}
            <Badge variant={member.plan.status === 'active' ? 'secondary' : 'outline'}>
              {member.plan.cancelAtPeriodEnd ? 'cancelling' : member.plan.status}
            </Badge>
          </>
        ) : (
          'No subscription'
        )}
      </Fact>
      <Fact label="Next box">{formatDate(member.nextBoxDate)}</Fact>
      <Fact label="Last order">
        {order
          ? `#${order.number ?? '?'} · ${order.parcelStatus ?? order.status ?? 'unknown'}`
          : 'None'}
      </Fact>
      {order && (
        <Fact label="Tracking">
          {order.trackingUrl ? (
            <a href={order.trackingUrl} target="_blank" rel="noreferrer" className="underline">
              {order.carrier ?? 'Track'} {order.trackingNumber ?? ''}
            </a>
          ) : (
            (order.trackingNumber ?? '—')
          )}
        </Fact>
      )}
    </div>
  );
}

function identityHint(identity: InboxIdentity | null, channel: Channel): string {
  if (channel === 'intercom') {
    return `No SBC account is linked to this ${CHANNEL_NAMES[channel]} contact. Link one by email if you know whose it is.`;
  }
  if (!identity) {
    return channel === 'whatsapp'
      ? 'No SBC account has this phone number. The draft asks them for their account email.'
      : 'No SBC account is linked. The draft asks them for their account email.';
  }
  if (identity.status === 'handoff') {
    return `${identity.claimedEmail ?? 'The email they gave'} failed the postcode check ${identity.failedAttempts} times. Check the account yourself and link it if it is theirs.`;
  }
  if (!identity.claimedEmail) {
    return 'The account matched by phone was unlinked. The draft asks them for their account email.';
  }
  return `They gave ${identity.claimedEmail}. The draft asks for the delivery postcode before it uses any account fact.`;
}

function IdentityLink({
  conversation,
  identity,
}: {
  conversation: InboxConversationDetail;
  identity: InboxIdentity | null;
}) {
  const link = useLinkInboxIdentity(conversation.id);
  const [email, setEmail] = useState('');
  const onLink = (event: FormEvent) => {
    event.preventDefault();
    const value = email.trim();
    if (value) {
      link.mutate({ email: value }, { onSuccess: () => setEmail('') });
    }
  };
  return (
    <>
      <p className="text-sm text-[var(--color-muted-foreground)]">
        {identityHint(identity, conversation.channel)}
      </p>
      <form className="flex gap-2" onSubmit={onLink}>
        <Input
          type="email"
          placeholder="member@example.com"
          aria-label="Member email"
          className="h-8"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button type="submit" size="sm" disabled={link.isPending || !email.trim()}>
          Link
        </Button>
      </form>
      {link.isError && (
        <p className="text-sm text-[var(--color-destructive)]">{link.error.message}</p>
      )}
    </>
  );
}
